import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  ProviderConfig,
  PROVIDER_BASE_URLS,
  PROVIDER_TYPES,
  copyProviderConfig,
  copyProviders,
  getNewProviderConfig,
  providerFallbackEnabled,
  setProviderFallback,
} from '@/features/master/masterSlice';

import FlexRow from '@/components/Common/FlexRow';

import {
  AddRow,
  GroupDivider,
  InfoBanner,
  Pill,
  SettingsInput,
  SettingsSelect,
  ToggleRow,
  su,
} from '@/pages/Settings/settingsUi';

// The edit buffer for one instance. `creds` only exists on a row the admin
// actually typed into — that is what implements the backend's omit-keeps-secret
// rule (mergeProviderCreds): a row posted WITHOUT `creds` keeps whatever is
// stored, a row posted WITH `creds` replaces them.
export interface ProviderEdit extends ProviderConfig {
  creds_dirty?:		boolean;
}

export function toProviderEdits(providers?: ProviderConfig[]): ProviderEdit[] {
  return copyProviders(providers).map((p) => ({
    ...p,
    creds:			{ username: "", password: "" },
    creds_dirty:	false,
  }));
}

export function newProviderEdit(): ProviderEdit {
  return {
    ...getNewProviderConfig(),
    creds:			{ username: "", password: "" },
    creds_dirty:	false,
  };
}

export function nameInvalid(prov: ProviderEdit, i: number, all: ProviderEdit[]): boolean {
  const name = (prov.name || '').trim();

  if (name === '')
    return true;

  // `name` is the identity — two instances sharing one would collide in the
  // adapter registry and in the provider:<name> channel list.
  return all.some((other, j) => j !== i && (other.name || '').trim() === name);
}

export function baseUrlInvalid(prov: ProviderEdit): boolean {
  const url = (prov.base_url || '').trim();

  if (url === '')
    return true;

  return !/^https?:\/\/[a-zA-Z0-9.\-:]+(\/.*)?$/.test(url);
}

export function typeInvalid(prov: ProviderEdit): boolean {
  return (prov.type || '').trim() === '';
}

export function providersInvalid(providers: ProviderEdit[]): boolean {
  return providers.some((p, i) =>
    nameInvalid(p, i, providers) || typeInvalid(p) || baseUrlInvalid(p)
  );
}

// Build the `providers[]` to POST. The posted list REPLACES the stored one
// (creds excepted), so this always emits the full desired list — dropping an
// entry is how you delete an instance, which the server rejects with 423 while
// its provider:<name> channel list is still attached to a project.
export function buildProvidersPayload(providers: ProviderEdit[]): ProviderConfig[] {
  return providers.map((prov) => {
    const out = copyProviderConfig(prov);

    out.name = (out.name || '').trim();
    out.base_url = (out.base_url || '').trim();
    out.country = (out.country || '').trim();

    // Read-only projection from GET — never send it back.
    delete out.creds_set;

    // Omit `creds` unless the admin actually typed something: that is what
    // keeps the stored password when only the URL changed.
    if (prov.creds_dirty &&
        ((prov.creds?.username || '') !== '' || (prov.creds?.password || '') !== '')) {
      out.creds = {
        username:	prov.creds?.username || '',
        password:	prov.creds?.password || '',
      };
    }

    return out;
  });
}

const typeDesc = (type: string): string => {
  const opt = PROVIDER_TYPES.find((o) => o.value === type);
  return opt ? opt.desc : type;
};

export interface ProviderRowProps {
  provider:			ProviderEdit;
  index:			number;
  all:				ProviderEdit[];
  triedSave:		boolean;
  isLoaded:			boolean;
  setProvider:		(prov: ProviderEdit) => any;
  requestDelete:	() => any;
}

export const ProviderRow = (props: ProviderRowProps) => {
  const { t } = useTranslation();

  const prov = props.provider;
  const [expanded, setExpanded] = React.useState(true);

  const update = (patch: Partial<ProviderEdit>) => {
    props.setProvider({ ...prov, ...patch });
  };

  const updateType = (val: string) => {
    // Prefill the known base URL when the field is still empty or still holds
    // the previous type's default, so the admin does not have to paste it.
    const oldDefault = PROVIDER_BASE_URLS[prov.type] || '';
    const newDefault = PROVIDER_BASE_URLS[val] || '';
    const keepUrl = (prov.base_url || '').trim();

    update({
      type:			val,
      base_url:		(keepUrl === '' || keepUrl === oldDefault) ? newDefault : keepUrl,
    });
  };

  const updateCreds = (patch: { username?: string; password?: string }) => {
    update({
      creds: {
        username:	patch.username !== undefined ? patch.username : (prov.creds?.username || ''),
        password:	patch.password !== undefined ? patch.password : (prov.creds?.password || ''),
      },
      creds_dirty:	true,
    });
  };

  return (
  <FlexRow direction="column"
           align="start"
           className="w-full rounded-[8px] border-solid border-1 border-[#E6E9EF]">

    {/* Header: name + type chip + running state, and the remove affordance. */}
    <FlexRow className="px-[16px] py-[12px] gap-[10px] w-full">
      <span className="material-icons !text-[20px] !text-[#8A93A3] cursor-pointer select-none"
            onClick={() => setExpanded((v) => !v)}>
        { expanded ? 'expand_more' : 'chevron_right' }
      </span>
      <span className="text-[15px] font-semibold text-[#1F2733] cursor-pointer select-none"
            onClick={() => setExpanded((v) => !v)}>
        { (prov.name || '').trim() || t('system:providers:untitled') }
      </span>
      <Pill tone="info" label={typeDesc(prov.type)} />
      <Pill tone={prov.disabled ? 'bad' : 'ok'}
            dot={true}
            label={prov.disabled
                     ? t('system:providers:status_disabled')
                     : t('system:providers:status_enabled')} />
      <FlexRow flex={1} />
      <FlexRow flex={0}
               className="gap-[6px] cursor-pointer select-none"
               onClick={() => props.requestDelete()}>
        <span className="material-icons !text-[16px] !text-[#D64545]">delete_outline</span>
        <span className="text-[13px] font-medium text-[#D64545]">
          { t('system:providers:remove') }
        </span>
      </FlexRow>
    </FlexRow>

    { expanded &&
    <FlexRow direction="column"
             align="start"
             className="px-[16px] pt-[18px] pb-[16px] gap-[18px] w-full border-t-1 border-solid border-[#EEF0F4]">

      <FlexRow align="start" className="gap-[16px] w-full">
        <SettingsInput label={t('system:providers:name:caption')}
                       required={true}
                       placeholder="KPN-1"
                       value={prov.name || ''}
                       invalid={props.triedSave && nameInvalid(prov, props.index, props.all)}
                       error={t('system:providers:name:invalid')}
                       hint={t('system:providers:name:hint')}
                       isLoaded={props.isLoaded}
                       setValue={(v) => update({ name: v })} />

        <SettingsSelect label={t('system:providers:type:caption')}
                        required={true}
                        options={PROVIDER_TYPES}
                        value={prov.type || ''}
                        invalid={props.triedSave && typeInvalid(prov)}
                        error={t('system:providers:type:invalid')}
                        isLoaded={props.isLoaded}
                        setValue={updateType} />
      </FlexRow>

      <SettingsInput label={t('system:providers:base_url:caption')}
                     required={true}
                     placeholder="https://marvin.bcms-cdn.prod.aws.kpn.com"
                     value={prov.base_url || ''}
                     invalid={props.triedSave && baseUrlInvalid(prov)}
                     error={t('system:providers:base_url:invalid')}
                     isLoaded={props.isLoaded}
                     setValue={(v) => update({ base_url: v })} />

      <FlexRow align="start" className="gap-[16px] w-full">
        <SettingsInput label={t('system:providers:country:caption')}
                       placeholder="NL"
                       value={prov.country || ''}
                       hint={t('system:providers:country:hint')}
                       isLoaded={props.isLoaded}
                       setValue={(v) => update({ country: v })} />
        <FlexRow flex={1} />
      </FlexRow>

      {/* --- Credentials. Blank = keep whatever the server already stores. --- */}
      <GroupDivider caption={t('system:providers:creds:caption')}
                    status={
                      <Pill tone={prov.creds_set ? 'ok' : 'warn'}
                            icon={prov.creds_set ? 'lock' : 'lock_open'}
                            label={prov.creds_set
                                     ? t('system:providers:creds:stored')
                                     : t('system:providers:creds:not_stored')} />
                    } />

      <FlexRow align="start" className="gap-[16px] w-full">
        <SettingsInput label={t('system:providers:creds:username')}
                       placeholder={prov.creds_set ? '••••••••' : ''}
                       value={prov.creds?.username || ''}
                       isLoaded={props.isLoaded}
                       setValue={(v) => updateCreds({ username: v })} />

        <SettingsInput label={t('system:providers:creds:password')}
                       type="password"
                       placeholder={prov.creds_set ? '••••••••' : ''}
                       value={prov.creds?.password || ''}
                       isLoaded={props.isLoaded}
                       setValue={(v) => updateCreds({ password: v })} />
      </FlexRow>

      <div className={su.hint + ' -mt-[8px]'}>
        { prov.creds_set
            ? t('system:providers:creds:hint_set')
            : t('system:providers:creds:hint_unset') }
      </div>

      {/* --- Guide source + on/off --- */}
      <GroupDivider caption={t('system:providers:options:caption')} />

      {/* The rich endpoint is labelled TEMP by the provider, so an operator
          needs a way to retreat to the thin one without a deploy. Writing it
          preserves any other keyed option on the instance. */}
      <ToggleRow value={providerFallbackEnabled(prov)}
                 caption={t('system:providers:fallback:caption')}
                 description={t('system:providers:fallback:hint')}
                 setValue={(v) => update({ urls: setProviderFallback(prov.urls, v) })} />

      <ToggleRow value={!!prov.disabled}
                 caption={t('system:providers:disabled:caption')}
                 setValue={(v) => update({ disabled: v })} />

      {/* Disabling stops the adapter but leaves its channels in every room's
          line-up — those zaps then fail upstream. Say so loudly. */}
      { prov.disabled &&
        <InfoBanner tone="warn" text={t('system:providers:disabled:warning')} />
      }
    </FlexRow>
    }
  </FlexRow>
  );
};

export interface ProvidersProps {
  providers:		ProviderEdit[];
  setProviders:		(providers: ProviderEdit[]) => any;
  triedSave:		boolean;
  isLoaded:			boolean;
}

// Presentational only: the edit buffer and the save live on the System page,
// which owns the single sticky action bar for the whole screen.
const Providers = (props: ProvidersProps) => {
  const { t } = useTranslation();

  const updateProvider = (i: number, prov: ProviderEdit) => {
    props.setProviders(props.providers.map((p, j) => (j === i ? prov : p)));
  };

  const deleteProvider = (i: number) => {
    props.setProviders(props.providers.filter((_, j) => j !== i));
  };

  const addProvider = () => {
    props.setProviders([...props.providers, newProviderEdit()]);
  };

  return (
  <React.Fragment>
    { props.providers.map((prov, i) =>
      <ProviderRow key={i}
                   provider={prov}
                   index={i}
                   all={props.providers}
                   triedSave={props.triedSave}
                   isLoaded={props.isLoaded}
                   setProvider={(p: ProviderEdit) => updateProvider(i, p)}
                   requestDelete={() => deleteProvider(i)} />
    )}

    { !props.providers.length &&
      <div className={su.subtitle}>
        { t('system:providers:empty') }
      </div>
    }

    <AddRow caption={t('system:providers:add')}
            onClick={addProvider} />

    {/* Deleting is "post the list without that instance", and the backend
        refuses while the channels are still in use — spell out the order. */}
    <InfoBanner text={t('system:providers:detach_hint')} />
  </React.Fragment>
  );
};

export default Providers;
